import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, Filter, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function DownloadReports({ user, onLogout }) {
  const [loading, setLoading] = useState(false);
  const [mentors, setMentors] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [courses, setCourses] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    mentor: 'all',
    courseProgress: 'all',
    fees: 'all'
  });
  
  // Selected fields for report
  const [selectedFields, setSelectedFields] = useState({
    // Basic Details
    firstName: true,
    lastName: true,
    email: true,
    schoolName: false,
    grade: false,
    mentor: false,
    phoneNumber: false,
    city: false,
    
    // Activity Details
    dailyStreak: false,
    totalProgress: false,
    lessonsCompleted: false,
    quizzesCompleted: false,
    
    // Fees
    feesPaid: false,
    feesRemaining: false,
    
    // Course Progress (will be dynamically added)
    courseProgress: false,
    
    // Custom Fields
    customFields: {}
  });

  useEffect(() => {
    fetchMentors();
    fetchCustomFields();
    fetchCourses();
  }, []);

  const fetchMentors = async () => {
    try {
      const res = await axios.get(`${API}/reports/mentors`, { headers: getAuthHeaders() });
      setMentors(res.data);
    } catch (error) {
      console.error('Failed to fetch mentors:', error);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const res = await axios.get(`${API}/custom-fields?include_hidden=true`, { headers: getAuthHeaders() });
      setCustomFields(res.data);
      
      // Initialize custom fields selection
      const customFieldsSelection = {};
      res.data.forEach(field => {
        customFieldsSelection[field.id] = false;
      });
      setSelectedFields(prev => ({
        ...prev,
        customFields: customFieldsSelection
      }));
    } catch (error) {
      console.error('Failed to fetch custom fields:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses`, { headers: getAuthHeaders() });
      setCourses(res.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const handleFieldToggle = (field) => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleCustomFieldToggle = (fieldId) => {
    setSelectedFields(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [fieldId]: !prev.customFields[fieldId]
      }
    }));
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${API}/reports/students`,
        { filters, selectedFields },
        { headers: getAuthHeaders() }
      );
      return res.data;
    } catch (error) {
      console.error('Failed to fetch report data:', error);
      toast.error('Failed to fetch report data');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    const data = await fetchReportData();
    if (!data || data.students.length === 0) {
      toast.error('No data to export');
      return;
    }

    const doc = new jsPDF('landscape');
    
    // Add header with filters and timestamp
    doc.setFontSize(16);
    doc.text('Student Report', 14, 15);
    
    doc.setFontSize(10);
    let yPos = 25;
    
    // Add filter information
    doc.text(`Filters Applied:`, 14, yPos);
    yPos += 6;
    
    if (filters.mentor !== 'all') {
      doc.text(`  • Mentor: ${filters.mentor}`, 14, yPos);
      yPos += 5;
    }
    if (filters.courseProgress !== 'all') {
      doc.text(`  • Course Progress: ${filters.courseProgress}`, 14, yPos);
      yPos += 5;
    }
    if (filters.fees !== 'all') {
      doc.text(`  • Fees: ${filters.fees === 'full' ? 'Full Fees Paid' : 'Partial Fees Paid'}`, 14, yPos);
      yPos += 5;
    }
    
    yPos += 3;
    doc.text(`Report Generated: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`, 14, yPos);
    
    // Prepare table data
    const headers = data.headers;
    const rows = data.students.map(student => 
      headers.map(header => student[header] || '-')
    );
    
    // Add table
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: yPos + 8,
      theme: 'grid',
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: headers.reduce((acc, _, idx) => {
        acc[idx] = { cellWidth: 'auto' };
        return acc;
      }, {})
    });
    
    doc.save(`student-report-${Date.now()}.pdf`);
    toast.success('PDF downloaded successfully!');
  };

  const generateCSV = async () => {
    const data = await fetchReportData();
    if (!data || data.students.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Add filter info at top
    let csvContent = 'Student Report\n\n';
    csvContent += 'Filters Applied:\n';
    if (filters.mentor !== 'all') csvContent += `Mentor: ${filters.mentor}\n`;
    if (filters.courseProgress !== 'all') csvContent += `Course Progress: ${filters.courseProgress}\n`;
    if (filters.fees !== 'all') csvContent += `Fees: ${filters.fees === 'full' ? 'Full Fees Paid' : 'Partial Fees Paid'}\n`;
    csvContent += `Report Generated: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST\n\n`;
    
    // Add headers
    csvContent += data.headers.join(',') + '\n';
    
    // Add rows
    data.students.forEach(student => {
      const row = data.headers.map(header => {
        const value = student[header] || '-';
        // Escape commas and quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvContent += row.join(',') + '\n';
    });
    
    // Create download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `student-report-${Date.now()}.csv`;
    link.click();
    
    toast.success('CSV downloaded successfully!');
  };

  const generateXLS = async () => {
    const data = await fetchReportData();
    if (!data || data.students.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Create metadata sheet
    const metaData = [
      ['Student Report'],
      [''],
      ['Filters Applied:']
    ];
    
    if (filters.mentor !== 'all') metaData.push([`Mentor: ${filters.mentor}`]);
    if (filters.courseProgress !== 'all') metaData.push([`Course Progress: ${filters.courseProgress}`]);
    if (filters.fees !== 'all') metaData.push([`Fees: ${filters.fees === 'full' ? 'Full Fees Paid' : 'Partial Fees Paid'}`]);
    metaData.push([`Report Generated: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`]);
    metaData.push(['']);
    metaData.push(data.headers);
    
    // Add student data
    data.students.forEach(student => {
      metaData.push(data.headers.map(header => student[header] || '-'));
    });
    
    const ws = XLSX.utils.aoa_to_sheet(metaData);
    XLSX.utils.book_append_sheet(wb, ws, 'Student Report');
    
    // Download
    XLSX.writeFile(wb, `student-report-${Date.now()}.xlsx`);
    toast.success('XLS downloaded successfully!');
  };

  return (
    <Layout user={user} onLogout={onLogout} currentPage="download-reports">
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0F1C2E] mb-2">Download Reports</h1>
          <p className="text-[#4D4D4D]">Apply filters and select fields to generate customized student reports</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Filters Card */}
          <Card className="lg:col-span-1 border-2 border-[#E7E7E7] shadow-lg rounded-2xl">
            <CardHeader className="premium-gradient text-white rounded-t-2xl">
              <CardTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Apply Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Mentor Filter */}
              <div>
                <Label className="text-sm font-semibold text-[#0F1C2E] mb-2 block">Filter by Mentor</Label>
                <Select value={filters.mentor} onValueChange={(value) => setFilters(prev => ({ ...prev, mentor: value }))}>
                  <SelectTrigger className="rounded-xl border-[#E7E7E7]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Students</SelectItem>
                    {mentors.map(mentor => (
                      <SelectItem key={mentor} value={mentor}>{mentor}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Course Progress Filter */}
              <div>
                <Label className="text-sm font-semibold text-[#0F1C2E] mb-2 block">Filter by Course Progress</Label>
                <Select value={filters.courseProgress} onValueChange={(value) => setFilters(prev => ({ ...prev, courseProgress: value }))}>
                  <SelectTrigger className="rounded-xl border-[#E7E7E7]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Progress</SelectItem>
                    <SelectItem value="0-50">0% - 50%</SelectItem>
                    <SelectItem value="50-100">50% - 100%</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Fees Filter */}
              <div>
                <Label className="text-sm font-semibold text-[#0F1C2E] mb-2 block">Filter by Fees</Label>
                <Select value={filters.fees} onValueChange={(value) => setFilters(prev => ({ ...prev, fees: value }))}>
                  <SelectTrigger className="rounded-xl border-[#E7E7E7]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Students</SelectItem>
                    <SelectItem value="full">Full Fees Paid</SelectItem>
                    <SelectItem value="partial">Partial Fees Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Fields Selection Card */}
          <Card className="lg:col-span-2 border-2 border-[#E7E7E7] shadow-lg rounded-2xl">
            <CardHeader className="premium-gradient text-white rounded-t-2xl">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Select Report Fields
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Details */}
                <div>
                  <h3 className="font-semibold text-[#0F1C2E] mb-4 text-lg border-b border-[#E7E7E7] pb-2">Basic Details</h3>
                  <div className="space-y-3">
                    {[
                      { key: 'firstName', label: 'First Name' },
                      { key: 'lastName', label: 'Last Name' },
                      { key: 'email', label: 'Email' },
                      { key: 'phoneNumber', label: 'Phone Number' },
                      { key: 'schoolName', label: 'School Name' },
                      { key: 'grade', label: 'Grade' },
                      { key: 'city', label: 'City' },
                      { key: 'mentor', label: 'Mentor' }
                    ].map(field => (
                      <div key={field.key} className="flex items-center space-x-2">
                        <Checkbox
                          id={field.key}
                          checked={selectedFields[field.key]}
                          onCheckedChange={() => handleFieldToggle(field.key)}
                        />
                        <label htmlFor={field.key} className="text-sm text-[#4D4D4D] cursor-pointer">
                          {field.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Activity & Fees */}
                <div>
                  <h3 className="font-semibold text-[#0F1C2E] mb-4 text-lg border-b border-[#E7E7E7] pb-2">Activity & Fees</h3>
                  <div className="space-y-3">
                    {[
                      { key: 'dailyStreak', label: 'Daily Streak' },
                      { key: 'totalProgress', label: 'Total Progress (%)' },
                      { key: 'lessonsCompleted', label: 'Lessons Completed' },
                      { key: 'quizzesCompleted', label: 'Quizzes Completed' },
                      { key: 'feesPaid', label: 'Fees Paid' },
                      { key: 'feesRemaining', label: 'Fees Remaining' },
                      { key: 'courseProgress', label: 'Course-wise Progress' }
                    ].map(field => (
                      <div key={field.key} className="flex items-center space-x-2">
                        <Checkbox
                          id={field.key}
                          checked={selectedFields[field.key]}
                          onCheckedChange={() => handleFieldToggle(field.key)}
                        />
                        <label htmlFor={field.key} className="text-sm text-[#4D4D4D] cursor-pointer">
                          {field.label}
                        </label>
                      </div>
                    ))}
                  </div>

                  {/* Custom Fields */}
                  {customFields.length > 0 && (
                    <>
                      <h3 className="font-semibold text-[#0F1C2E] mb-4 mt-6 text-lg border-b border-[#E7E7E7] pb-2">Custom Fields</h3>
                      <div className="space-y-3">
                        {customFields.map(field => (
                          <div key={field.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`custom-${field.id}`}
                              checked={selectedFields.customFields[field.id]}
                              onCheckedChange={() => handleCustomFieldToggle(field.id)}
                            />
                            <label htmlFor={`custom-${field.id}`} className="text-sm text-[#4D4D4D] cursor-pointer">
                              {field.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Download Options */}
        <Card className="mt-6 border-2 border-[#E7E7E7] shadow-lg rounded-2xl">
          <CardHeader className="premium-gradient text-white rounded-t-2xl">
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Download Report
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={generatePDF}
                disabled={loading}
                className="bg-primary-blue hover:bg-[#1873CC] text-white px-6 py-3 rounded-xl font-semibold shadow-md transition-all disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5 mr-2" />
                    Download PDF
                  </>
                )}
              </Button>

              <Button
                onClick={generateCSV}
                disabled={loading}
                className="bg-primary-blue hover:bg-[#1873CC] text-white px-6 py-3 rounded-xl font-semibold shadow-md transition-all disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="w-5 h-5 mr-2" />
                    Download CSV
                  </>
                )}
              </Button>

              <Button
                onClick={generateXLS}
                disabled={loading}
                className="bg-primary-blue hover:bg-[#1873CC] text-white px-6 py-3 rounded-xl font-semibold shadow-md transition-all disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="w-5 h-5 mr-2" />
                    Download XLS
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
